import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useScrollIntoViewElementId } from '@portal/shared/hooks/useScrollIntoViewElementId';
import { StarRatings as StarRatingsProps, SanityKeyed } from 'types/studio/studio';
import { Button, RatingStars, Typography } from '@ag1/ui';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import styles from './StarRatings.module.scss';
import { CommonSectionProps } from '../../types';

type StarRatingsOverrideConfig = {
  darkMode?: boolean;
  disableNegativeBottomMargin?: boolean;
  noStarOutline?: boolean;
};

const StarRatings = (
  props: SanityKeyed<StarRatingsProps> & CommonSectionProps & StarRatingsOverrideConfig
) => {
  const {
    _key: key,
    starsCount,
    reviewCount,
    link,
    scrollToReviews,
    text,
    darkMode = false,
    disableNegativeBottomMargin = false,
    noStarOutline = false
  } = props;
  const locale = useLocale();
  const { scrollIntoView } = useScrollIntoViewElementId();

  const scrollLink = useMemo(
    () => (scrollToReviews ? `#${scrollToReviews}` : undefined),
    [scrollToReviews]
  );

  const handleReviewClick = useCallback(
    (e: React.MouseEvent) => {
      if (scrollToReviews) {
        e.preventDefault();
        scrollIntoView(scrollToReviews);
      }
    },
    [scrollIntoView, scrollToReviews]
  );

  return (
    <div
      id={key}
      className={`${styles.root} ${disableNegativeBottomMargin ? styles.noNegativeMargin : ''}`}>
      <RatingStars
        rating={starsCount}
        thin
        starClassName={clsx(styles.stars, noStarOutline && styles.noStarOutline)}
      />
      <div className={styles.content}>
        {link && (
          <Button
            variant={darkMode ? 'linkWhite' : 'link'}
            href={scrollLink || getSanityLinkUrl(link, locale)}
            size={{ s: 'medium', xxs: 'small' }}
            noPadding
            onClick={handleReviewClick}
            className={clsx(styles.link, darkMode && styles.darkModeText)}>
            {reviewCount}
          </Button>
        )}
        {text && (
          <Typography
            variant="portalCaption"
            size="2"
            component="p"
            noPadding
            className={clsx(styles.text, darkMode && styles.darkModeText)}>
            {text}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default StarRatings;
